/**
 * useStepNav — per-step navigation + gating for the CartSidebar bottom bar.
 *
 * Extracted from StepNavSection so the routing/gating/label logic lives in one
 * place and StepNavSection can stay presentational. The skip-aware step
 * sequence + next/prev decision come from `useResolvedStepFlow`; this hook
 * layers on:
 *   - the TRUTHFUL primary label, derived from the ACTUAL `nextStep` (not a
 *     hardcoded guess that lies when the flow skips configuration/instructions),
 *   - the per-step disabled gating + tooltip copy,
 *   - the navigation + analytics handlers (`STEP_ADVANCED` / `STEP_RETURNED`,
 *     with `targetTab` for the Instructions smart-back).
 *
 * Legacy-context isolation (IMPORTANT — do not regress):
 *   `usePurchaseFlowPlaceOrder` / `usePurchaseFlowOrderMeta` are constate
 *   contexts mounted ONLY inside `LegacyStepShell` (instructions / billing /
 *   summary routes). This hook runs on EVERY step — catalogue + configuration
 *   included — so it MUST NOT call those hooks, or constate fires a
 *   "must be wrapped with Provider" warning on every catalogue/configuration
 *   render. The Summary place-order gate that needs them stays in the
 *   `SummaryNavButtons` sub-component, which only mounts when step === 'summary'.
 *   This hook fully owns catalogue / configuration / instructions / billing and
 *   provides summary's store-based place-order handler + back-to-billing nav,
 *   neither of which touches the legacy contexts.
 */

import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { cartProductKinds } from 'dataQueries/purchaseFlowRevampProductCodes'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { logger } from 'utils/logging'
import type { PfStep } from 'models/purchaseFlowRevamp'

import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'
import { useUserData } from 'components/contexts/UserDataContext'

import { usePfStore } from '../../store'
import {
  selectBillingStepErrorsFor,
  selectBillingStepValidFor,
  selectConfigurationStepErrors,
  selectPendingConfigurationTabKeys,
  selectConfigurationStepValid,
  selectInstructionsStepErrors,
  selectInstructionsStepValid
} from '../../store/selectors'
import { useInstructionOptions } from 'dataQueries/purchaseFlowRevampInstructions.query'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import type { StepAdvancedPayload } from '../../analytics'
import { onSiteContactIsOrderer, resolvePrefillTarget } from '../../steps/instructions/contactPrefill.helpers'
import { resolveDefaultCcEmails, resolveEffectiveCcEmails } from '../../steps/billing/billingDefaults'
import { PfStepPath, usePfPath } from '../../routing/pfBasePath'
import { usePfEmbedConsentState } from '../../routing/usePfEmbedConsent'
import { resolveNextStep, resolvePrevStep, useResolvedStepFlow } from '../../routing/useResolvedStepFlow'
import { useBillingPaymentMethods } from '../../steps/billing/useBillingPaymentMethods'
import { useDefaultInvoicingAddress } from '../../steps/billing/useDefaultInvoicingAddress'
import { refineInstructionsErrors } from './refineInstructionsErrors'
import { configurationTabLabels } from '../../steps/configuration/chipLabels'
import { resolveTabsFromCartAndCatalogue } from '../../steps/configuration/resolveTabs'

export type StepNavStep = PfStep

/** Instructions gaps with copy of their own; any other gap keeps the generic message. */
const BLOCKED_REASON_KEYS = {
  favourite_creative_missing: 'purchase_flow_revamp:cart.blocked.favourite_creative_missing',
  meeting_type_missing: 'purchase_flow_revamp:cart.blocked.meeting_type_missing',
  meeting_name_invalid: 'purchase_flow_revamp:cart.blocked.meeting_name_invalid',
  meeting_phone_invalid: 'purchase_flow_revamp:cart.blocked.meeting_phone_invalid',
  meeting_email_invalid: 'purchase_flow_revamp:cart.blocked.meeting_email_invalid',
  meeting_date_invalid: 'purchase_flow_revamp:cart.blocked.meeting_date_invalid',
  meeting_minutes_missing: 'purchase_flow_revamp:cart.blocked.meeting_minutes_missing',
  meeting_address_invalid: 'purchase_flow_revamp:cart.blocked.meeting_address_invalid',
  prices_recalculating: 'purchase_flow_revamp:cart.blocked.prices_recalculating',
} as const satisfies Partial<Record<NextStepValidationError, string>>

type BlockedReasonCode = keyof typeof BLOCKED_REASON_KEYS

/** The element on the Instructions page that holds a gap, marked with `data-pf-gap`, or null when it is not on screen. */
export const findGapElement = (gap: NextStepValidationError): Element | null =>
  document.querySelector(`[data-pf-gap~="${gap}"]`)

/**
 * The gap the blocked Next talks about: the first one with copy that is on screen, so the message, the scroll and
 * the outline all point at the same field. `located` is false when none of them is on screen; the message then
 * names the first gap with copy and does not promise a highlight.
 */
export const pickInstructionsGap = (errors: NextStepValidationError[]): { gap: BlockedReasonCode | undefined; located: boolean } => {
  const named = errors.filter((code): code is BlockedReasonCode => code in BLOCKED_REASON_KEYS)
  const onScreen = named.find((code) => code !== 'prices_recalculating' && findGapElement(code) !== null)
  return onScreen ? { gap: onScreen, located: true } : { gap: named[0], located: false }
}


export interface StepNav {
  /** The step the primary action navigates to (`null` on summary). */
  nextStep: PfStep | null
  /** Truthful primary label, derived from `nextStep`. `null` on summary (the
   *  summary step renders its own place-order button via `SummaryNavButtons`). */
  primaryLabel: string | null
  /** Compact "To {destination}" label for the mobile footer bar, where the
   *  progress stepper already supplies wayfinding so the button can stay short.
   *  `null` on summary (handled by `SummaryNavButtons`). */
  primaryLabelShort: string | null
  primaryDisabled: boolean
  /** Tooltip / inline reason shown on the disabled primary. `''` when enabled. */
  primaryReason: string
  /** The toast a click on the disabled primary shows. `''` when enabled. */
  primaryBlockedMessage: string
  onPrimary: () => void
  /** A click on the disabled primary: reports why the step is blocked. */
  onPrimaryBlocked: () => void
  secondaryLabel: string | null
  onSecondary: () => void
  /** True on the summary step — caller renders `SummaryNavButtons` instead of
   *  the generic primary so the legacy place-order contexts stay isolated. */
  isSummary: boolean
  /** Property-selection drawer state — owned here because the catalogue primary
   *  gate opens it instead of navigating when no property is selected (F1). */
  isPropertyDrawerOpen: boolean
  setPropertyDrawerOpen: (open: boolean) => void
}

/**
 * Per-step navigation + gating for the cart bottom bar. `step` is the PF revamp
 * step the bar is rendering for (in practice the store's `currentStep`, but the
 * hook resolves targets for the passed `step` so it stays correct regardless).
 */
export const useStepNav = (step: StepNavStep): StepNav => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const navigate = useNavigate()
  const pfPath = usePfPath()
  const { ensureConsentRecorded } = usePfEmbedConsentState()
  const trackAnalytics = usePfRevampAnalytics()

  const items = usePfStore((s) => s.items)
  const property = usePfStore((s) => s.property)
  const meetingMinutesMissing = usePfStore((s) => s.meetingMinutesMissing)
  const meetingModeImplied = usePfStore((s) => s.meetingModeImplied)
  const { baseUserData, clientData, currentWorkspaceCcEmails } = useUserData()

  // Skip-aware sequence + first configurable tuple (single source of truth).
  const { firstConfigTarget, steps } = useResolvedStepFlow()
  const isInstructionsStepSkipped = !steps.includes('instructions')

  // Resolve next/prev for THIS `step` (not necessarily the store's currentStep —
  // keeps the hook correct under tests that render an explicit step).
  const flowContext = useMemo(
    () => ({ firstConfigTarget, isInstructionsStepSkipped }),
    [firstConfigTarget, isInstructionsStepSkipped]
  )
  const nextStep = resolveNextStep(step, flowContext)
  const prevStep = resolvePrevStep(step, flowContext)

  // Catalogue + flag for the per-step validity gates.
  const catalogue = usePfCatalogue().data
  const allowFallbackFlowResolver = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)

  const isConfigStepValid = usePfStore(
    useMemo(() => selectConfigurationStepValid(catalogue, allowFallbackFlowResolver), [catalogue, allowFallbackFlowResolver])
  )
  const pendingConfigTabKeys = usePfStore(
    useMemo(() => selectPendingConfigurationTabKeys(catalogue, allowFallbackFlowResolver), [catalogue, allowFallbackFlowResolver])
  )
  // The tabs still to configure, named as their chips are, so the disabled Proceed says which (QA-664).
  const pendingConfigLabels = useMemo(() => {
    if (!catalogue || !pendingConfigTabKeys) return []
    const tabs = resolveTabsFromCartAndCatalogue(items, catalogue.shellProducts, allowFallbackFlowResolver)
    const labels = configurationTabLabels(
      tabs,
      (cartItemId) => {
        const shellId = items.find((item) => item.id === cartItemId)?.shellProductId
        return catalogue.shellProducts.find((shell) => shell.id === shellId)?.displayName ?? cartItemId
      },
      (flowId) => t(`purchase_flow_revamp:configuration.flow_label.${flowId}`)
    )
    const pending = new Set(pendingConfigTabKeys.split(','))
    return tabs.flatMap((tab, index) => (pending.has(`${tab.cartItemId}|${tab.flowId}`) ? [labels[index]] : []))
  }, [catalogue, pendingConfigTabKeys, items, allowFallbackFlowResolver, t])
  // The gate must see the same verdict the resolver does, or an invisible step
  // still demands a meeting that has no UI left to set — the deadlock DF-I8's
  // short-circuit exists to prevent. Shares the resolver's query cache entry.
  const instructionsVisible = useInstructionOptions().data?.visible
  const isInstructionsStepValid = usePfStore(
    useMemo(() => selectInstructionsStepValid(catalogue, instructionsVisible), [catalogue, instructionsVisible])
  )
  // This hook owns the ACTUAL Billing "Next" gate, so it must
  // see the same BE `isDefault` preselect the page renders, or a visibly-selected
  // payment method would sit next to a disabled button. Same shared React Query
  // cache entry `useResolvedStepFlow` (mounted just above) already fetches — no
  // extra request. PM-D2 REVERSAL (ratified, option A): Summary's Place Order
  // now ALSO accepts this same preselect — `SummaryNavButtons` in
  // `StepNavSection.component.tsx` reads its own `useBillingPaymentMethods()`
  // and threads `defaultPaymentMethod` into `selectSummaryStepValid`, so the
  // two gates agree instead of a checked-but-unclicked radio dead-ending at
  // Summary.
  const { defaultPaymentMethod } = useBillingPaymentMethods()
  // The sole-saved-address default, so a client who never clicked
  // the already-highlighted card is not stopped by a dead Proceed button.
  const defaultInvoicingAddress = useDefaultInvoicingAddress()
  // The kinds decide whether the order is chargeable at all, and a
  // measurement line is chargeable whatever its catalogue price says.
  const cartKinds = useMemo(() => cartProductKinds(items, catalogue), [items, catalogue])
  const isBillingStepValid = usePfStore(
    useMemo(
      () => selectBillingStepValidFor(defaultPaymentMethod, cartKinds, defaultInvoicingAddress),
      [defaultPaymentMethod, cartKinds, defaultInvoicingAddress]
    )
  )

  const isEmpty = items.length === 0
  const isSummary = step === 'summary'

  // F1: property-required gate on the catalogue step opens this drawer instead
  // of navigating forward.
  const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = useState(false)

  // ── Navigation helper ───────────────────────────────────────────────────────

  const navigateToStep = useCallback((target: PfStep) => {
    switch (target) {
      case 'catalogue':
        navigate(pfPath(PfStepPath.CATALOGUE))
        return
      case 'configuration':
        // `configuration` is only ever a target when a configurable tuple
        // exists; guard anyway so a missing target never builds a bad URL.
        if (firstConfigTarget) {
          navigate(pfPath(`${PfStepPath.CONFIGURATION}/${firstConfigTarget.cartItemId}/${firstConfigTarget.flowId}`))
        }
        return
      case 'instructions':
        navigate(pfPath(PfStepPath.INSTRUCTIONS))
        return
      case 'billing':
        navigate(pfPath(PfStepPath.BILLING))
        return
      case 'summary':
        navigate(pfPath(PfStepPath.SUMMARY))
        return
    }
  }, [navigate, pfPath, firstConfigTarget])

  // ── Next-click analytics ────────────────────────────────────────────────────

  /** What the client leaves billing and instructions with, read at the click like legacy's next click. */
  const stepLeaveProps = useCallback((): Pick<StepAdvancedPayload, 'ccEmailsAddedCount' | 'onSiteContactIsOrderer'> => {
    const state = usePfStore.getState()
    if (step === 'billing') {
      const prefill = resolveDefaultCcEmails(clientData?.ccEmails, currentWorkspaceCcEmails)
      return { ccEmailsAddedCount: resolveEffectiveCcEmails(state.ccEmails, state.ccEmailsTouched, prefill).length }
    }
    if (step !== 'instructions') return {}
    const prefillTarget = resolvePrefillTarget({ targetUserEmail: state.targetUserEmail, user: baseUserData })
    return { onSiteContactIsOrderer: onSiteContactIsOrderer(state.meeting, prefillTarget) }
  }, [step, baseUserData, clientData?.ccEmails, currentWorkspaceCcEmails])

  const trackBlocked = useCallback((validationErrors: NextStepValidationError[], revealedGap?: NextStepValidationError | null) => {
    trackAnalytics({
      type: PfRevampEventName.STEP_ADVANCE_BLOCKED,
      // No code for a closed gate means the gate and its codes drifted apart; legacy reports that the same way.
      payload: {
        fromStep: step,
        toStep: nextStep ?? step,
        validationErrors: validationErrors.length > 0 ? validationErrors : ['unexplained_block'],
        ...(revealedGap !== undefined && { revealedGap }),
      },
    })
  }, [step, nextStep, trackAnalytics])

  /** The codes behind this step's gate, earlier gaps first as legacy reports them. */
  const blockedErrors = useCallback((): NextStepValidationError[] => {
    const state = usePfStore.getState()
    if (step === 'catalogue' && !property) return ['address_missing']
    if (state.items.length === 0) return ['product_missing']
    switch (step) {
      case 'configuration': return selectConfigurationStepErrors(catalogue, allowFallbackFlowResolver)(state)
      case 'instructions': return refineInstructionsErrors(selectInstructionsStepErrors(catalogue, instructionsVisible)(state), state)
      case 'billing': return selectBillingStepErrorsFor(defaultPaymentMethod, cartKinds, defaultInvoicingAddress)(state)
      default: return []
    }
    // The two UI flags are read off the state above; listed so the hint follows them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, property, catalogue, allowFallbackFlowResolver, instructionsVisible, defaultPaymentMethod, cartKinds, defaultInvoicingAddress, meetingMinutesMissing, meetingModeImplied])

  const revealInstructionsGaps = usePfStore((s) => s.revealInstructionsGaps)

  // On Instructions the click also shows where: every open gap is outlined, and the page scrolls to the first.
  const onPrimaryBlocked = useCallback(() => {
    const errors = blockedErrors()
    if (step !== 'instructions') {
      trackBlocked(errors)
      return
    }
    const { gap, located } = pickInstructionsGap(errors)
    trackBlocked(errors, located ? gap : null)
    revealInstructionsGaps(errors)
    if (located && gap) findGapElement(gap)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [trackBlocked, blockedErrors, step, revealInstructionsGaps])

  // ── Primary action ────────────────────────────────────────────────────────

  const onPrimary = useCallback(async () => {
    // Catalogue: require a property before advancing — open the drawer instead
    // of navigating (F1). The label/gate are unchanged; only the side-effect.
    if (step === 'catalogue' && !property) {
      trackBlocked(['address_missing'])
      setIsPropertyDrawerOpen(true)
      return
    }

    // Summary: fire the legacy place-order callback the
    // `PlaceOrderHandlerPublisher` (mounted in LegacyStepShell) wrote to the
    // store. Store + window only — never touches the legacy constate contexts,
    // so it is safe to live in this always-mounted hook.
    if (step === 'summary') {
      trackAnalytics({
        type: PfRevampEventName.STEP_ADVANCED,
        payload: { fromStep: 'summary', toStep: 'summary' },
      })
      // Record the acceptance BEFORE the order goes out: a failure here must
      // stop the placement, not follow it. Resolves true immediately when no
      // consent was due, which is every order outside the CRM embed.
      if (!await ensureConsentRecorded()) {
        logger.warn(
          '[pf-revamp] Place order blocked: embed consent could not be recorded',
          undefined,
          { context: { component: 'useStepNav', action: 'consentNotRecorded' } }
        )
        return
      }

      const placeOrderHandler = usePfStore.getState().placeOrderHandler
      if (placeOrderHandler) {
        placeOrderHandler()
        return
      }
      // Fallback — legacy bridge not yet mounted (e.g. Summary tree mid-mount).
      // Scroll to the legacy submit button so the user can still complete the
      // order. Single warning, not a hard rejection.
      logger.warn(
        '[pf-revamp] StepNavSection: place-order handler missing, falling back to scroll',
        undefined,
        { context: { component: 'StepNavSection', action: 'fallback-scroll' } }
      )
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
      }
      return
    }

    if (!nextStep) return
    const leaveProps = stepLeaveProps()
    navigateToStep(nextStep)
    trackAnalytics({
      type: PfRevampEventName.STEP_ADVANCED,
      payload: { fromStep: step, toStep: nextStep, ...leaveProps },
    })
  }, [step, property, nextStep, navigateToStep, trackAnalytics, ensureConsentRecorded, trackBlocked, stepLeaveProps])

  // ── Secondary action ────────────────────────────────────────────────────────

  const onSecondary = useCallback(() => {
    if (!prevStep) return
    navigateToStep(prevStep)
    // Smart-back from Instructions to a specific configuration tab carries
    // `targetTab` so funnels can tell smart-back hits from generic-back (OQ#7).
    if (step === 'instructions' && prevStep === 'configuration' && firstConfigTarget) {
      trackAnalytics({
        type: PfRevampEventName.STEP_RETURNED,
        payload: {
          fromStep: step,
          toStep: prevStep,
          targetTab: { cartItemId: firstConfigTarget.cartItemId, flowId: firstConfigTarget.flowId },
        },
      })
      return
    }
    trackAnalytics({
      type: PfRevampEventName.STEP_RETURNED,
      payload: { fromStep: step, toStep: prevStep },
    })
  }, [step, prevStep, firstConfigTarget, navigateToStep, trackAnalytics])

  // ── Truthful primary label (derived from the actual nextStep) ───────────────
  //
  // THE FIX: catalogue's nextStep can be configuration | instructions | billing,
  // and configuration's can be instructions | billing — the label now follows
  // the real destination instead of a hardcoded "proceed_to_configuration" /
  // "proceed_to_instructions" that lied when the flow skipped a step.

  const primaryLabel = (() => {
    switch (nextStep) {
      case 'configuration': return t('purchase_flow_revamp:cart.proceed_to_configuration')
      case 'instructions': return t('purchase_flow_revamp:cart.proceed_to_instructions')
      case 'billing': return t('purchase_flow_revamp:cart.proceed_to_billing')
      case 'summary': return t('purchase_flow_revamp:cart.proceed_to_summary')
      default: return null
    }
  })()

  const primaryLabelShort = (() => {
    switch (nextStep) {
      case 'configuration': return t('purchase_flow_revamp:cart.short_to_configuration')
      case 'instructions': return t('purchase_flow_revamp:cart.short_to_instructions')
      case 'billing': return t('purchase_flow_revamp:cart.short_to_billing')
      case 'summary': return t('purchase_flow_revamp:cart.short_to_summary')
      default: return null
    }
  })()

  const secondaryLabel = (() => {
    switch (prevStep) {
      case 'catalogue': return t('purchase_flow_revamp:cart.back_to_catalogue')
      case 'configuration': return t('purchase_flow_revamp:cart.back_to_configuration')
      case 'instructions': return t('purchase_flow_revamp:cart.back_to_instructions')
      case 'billing': return t('purchase_flow_revamp:cart.back_to_billing')
      default: return null
    }
  })()

  // ── Gating ──────────────────────────────────────────────────────────────────

  const primaryDisabled = (() => {
    switch (step) {
      case 'catalogue': return isEmpty
      case 'configuration': return isEmpty || !isConfigStepValid
      case 'instructions': return isEmpty || !isInstructionsStepValid
      case 'billing': return isEmpty || !isBillingStepValid
      // Summary's disabled state (Zustand validity + Stripe gate) is owned by
      // SummaryNavButtons; this branch is unused for summary.
      case 'summary': return false
    }
  })()

  /** Tooltip surfaced on the disabled primary. Empty string = no tooltip. */
  const primaryReason = (() => {
    if (!primaryDisabled) return ''
    switch (step) {
      case 'catalogue':
        // Property-missing wins — it's the deeper blocker. Otherwise empty-cart
        // shows an "add a product" hint so the disabled primary explains itself.
        if (!property) return t('purchase_flow_revamp:cart.proceed_requires_property')
        return isEmpty ? t('purchase_flow_revamp:cart.proceed_requires_cart_items') : ''
      case 'configuration':
        if (isEmpty) return t('purchase_flow_revamp:cart.proceed_requires_cart_items')
        return pendingConfigLabels.length > 0
          ? t('purchase_flow_revamp:cart.proceed_requires_configuration_of', { products: pendingConfigLabels.join(', ') })
          : t('purchase_flow_revamp:cart.proceed_requires_configuration_complete')
      case 'instructions': {
        if (isEmpty) return t('purchase_flow_revamp:cart.proceed_requires_cart_items')
        // Name the gap the user can fix, and offer to show it only when it is on the page.
        const { gap, located } = pickInstructionsGap(blockedErrors())
        if (!gap) return t('purchase_flow_revamp:cart.proceed_requires_instructions_complete')
        return located
          ? `${t(BLOCKED_REASON_KEYS[gap])} ${t('purchase_flow_revamp:cart.blocked.click_to_show')}`
          : t(BLOCKED_REASON_KEYS[gap])
      }
      case 'billing':
        return isEmpty
          ? t('purchase_flow_revamp:cart.proceed_requires_cart_items')
          : t('purchase_flow_revamp:cart.proceed_requires_billing_complete')
      case 'summary': return ''
    }
  })()

  // After the click the gap is outlined on the page, so the toast says where to look instead of "click".
  const primaryBlockedMessage = (() => {
    if (!primaryDisabled || step !== 'instructions' || isEmpty) return primaryReason
    const { gap, located } = pickInstructionsGap(blockedErrors())
    if (!gap || !located) return primaryReason
    return `${t(BLOCKED_REASON_KEYS[gap])} ${t('purchase_flow_revamp:cart.blocked.highlighted_note')}`
  })()

  return {
    nextStep,
    primaryLabel,
    primaryLabelShort,
    primaryDisabled,
    primaryReason,
    primaryBlockedMessage,
    onPrimary,
    onPrimaryBlocked,
    secondaryLabel,
    onSecondary,
    isSummary,
    isPropertyDrawerOpen,
    setPropertyDrawerOpen: setIsPropertyDrawerOpen,
  }
}
